<script setup>
import { computed, nextTick, onMounted, watch } from 'vue';
import VueApexCharts from 'vue3-apexcharts';
import { categories, fiveElementColors, chartColors } from './FiveChart';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  data: {
    type: Object,
    default: () => ({}),
  },
});

// 원국 (bar)
const baseBarData = computed(() => {
  const data = props.data?.원국 || {};
  return categories.map((key) => ({
    x: key,
    y: data?.[key] ?? 0,
    fillColor: fiveElementColors[key],
  }));
});

// 행운 (line)
const jijangLineData = computed(() => {
  const data = props.data?.행운 || {};
  return categories.map((key) => ({
    x: key,
    y: data?.[key] ?? 0,
  }));
});

const series = computed(() => [
  { name: '타고난 오행', type: 'column', data: baseBarData.value },
  { name: '2026년', type: 'line', data: jijangLineData.value },
]);

const labelColors = computed(() => categories.map((key) => fiveElementColors[key] || '#000'));

const chartOptions = computed(() => {
  const activeColors = chartColors.slice(0, series.value.length);

  return {
    chart: {
      type: 'line',
      stacked: false,
      toolbar: { show: false },
      zoom: { enabled: false },
      parentHeightOffset: 0,
      width: '100%',
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        borderRadiusWhenStacked: 'last',
        columnWidth: '28px',
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: -12,
      style: {
        colors: labelColors.value,
        fontSize: '12px',
        fontWeight: 700,
      },
      background: {
        enabled: false,
      },
      formatter: (val, { seriesIndex }) => {
        if (seriesIndex === 1) return `${val}%`;
        return '';
      },
    },
    colors: activeColors,
    xaxis: {
      categories,
      axisTicks: { show: false },
      axisBorder: { show: false },
      tickPlacement: 'between',
      labels: {
        style: {
          colors: labelColors.value,
          fontFamily: 'Pretendard',
          fontSize: '14px',
          fontWeight: '700',
        },
      },
    },
    yaxis: {
      tickAmount: 4,
      labels: {
        padding: 8,
        formatter: (val) => (val === 0 ? '0' : Math.round(val)),
      },
    },
    legend: {
      position: 'bottom',
      onItemClick: {
        toggleDataSeries: false,
      },
    },
    grid: {
      padding: {
        top: 4,
        bottom: 16,
        left: -4,
      },
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: (val) => val.toString(),
      },
    },
    stroke: {
      width: [0, 2],
      curve: ['smooth', 'smooth'],
      colors: activeColors,
    },
    markers: {
      size: 4,
      strokeWidth: 2,
      fillOpacity: 1,
      colors: ['#fff'],
      strokeColors: activeColors,
    },
  };
});

const updateChartStyles = () => {
  nextTick(() => {
    const labels = document.querySelectorAll('.apexcharts-data-labels text');
    const legendMarkers = document.querySelectorAll('.apexcharts-legend-marker');
    const legendText = document.querySelectorAll('.apexcharts-legend-text');

    const urls = [
      '/images/chart/bar-chart__legend.svg',
      '/images/chart/line-chart__legend-orange.svg',
    ];

    labels.forEach((label, index) => {
      const key = categories[index % categories.length];
      const color = fiveElementColors[key] || '#000';
      label.setAttribute('fill', color);
      label.style.fontWeight = '700';
    });

    legendMarkers.forEach((marker, index) => {
      marker.style.background = `url(${urls[index]}) no-repeat center/contain`;
      marker.style.borderRadius = '0';
      marker.style.width = '22px';
      marker.style.height = '12px';
    });
    legendText.forEach((el) => {
      el.style.color = '#333';
      el.style.fontSize = '14px';
      el.style.fontFamily = 'Pretendard';
      el.style.fontWeight = '400';
      el.style.lineHeight = '18px';
    });
  });
};

onMounted(updateChartStyles);

watch(() => props.data, updateChartStyles, { deep: true });
</script>

<template>
  <div class="yearly-fortune-chart">
    <div class="chart-container">
      <VueApexCharts type="line" height="250" :options="chartOptions" :series="series" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.yearly-fortune-chart {
  width: 100%;
}

.chart-container {
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  overflow: hidden;
}

.chart-container :deep(.apexcharts-canvas) {
  max-width: 100% !important;
  width: 100% !important;
}

:deep(.apexcharts-data-labels text) {
  stroke: white;
  stroke-width: 2px;
  paint-order: stroke;
}

:deep(.apexcharts-tooltip) {
  border-radius: 5px;
}

:deep(.apexcharts-tooltip-marker) {
  background-color: transparent !important;
}
</style>
